import { QrcodeOutlined } from '@ant-design/icons';
import { Button, Tooltip, message } from 'antd';
import * as React from 'react';

import {
  PagamentoWithDettaglioDTO,
  getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions,
  getGetFrontRichiesteIdPagamentiQueryKey,
} from '@framework/api-client';
import { getNetworkErrorMessage } from '@framework/auth';
import {
  useAuthDownload,
  useHasDocumentsInlineViewer,
} from '@framework/common-web';
import { useTranslation } from '@framework/i18n';
import { useQueryClient } from 'react-query';
import { EnumCodStatoPagamento } from '../../burden/payments/EnumCodStatoPagamento';
import { isPaymentLockedToDifferentMethod } from './isPaymentLockedToMethod';

interface DownloadPaymentAlertButtonProps {
  payment: PagamentoWithDettaglioDTO;
}

export function DownloadPaymentAlertButton(
  props: DownloadPaymentAlertButtonProps,
) {
  const { payment } = props;
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const hasInlineViewer = useHasDocumentsInlineViewer();
  const { download, isFetching } = useAuthDownload(
    getGetFrontRichiesteIdPagamentiIdAvvisoQueryOptions,
    [payment.idRichiesta!, payment.id!],
    {
      onSuccess: () => {
        queryClient.invalidateQueries(
          getGetFrontRichiesteIdPagamentiQueryKey(payment.idRichiesta!),
        );
      },
      onError: e => {
        message.error(
          getNetworkErrorMessage(
            e,
            t(
              'form.errore-in-download',
              'Si è verificato un problema durante il download del file',
            ),
          ),
        );
      },
      showInlineViewer: hasInlineViewer,
    },
  );

  if (isPaymentLockedToDifferentMethod(payment, 'AVVISO')) return null;

  if (payment.codStatoPagamento === EnumCodStatoPagamento.SUCCESSO) return null;

  // Blocchiamo i pagamenti in errore poiché GovPay non permette di ripetere i pagamenti
  if (payment.codStatoPagamento === EnumCodStatoPagamento.FALLIMENTO)
    return null;

  if (!hasPayment(payment)) return null;

  return (
    <Tooltip title={t('form.scarica-avviso', 'Scarica Avviso')}>
      <Button
        icon={
          <QrcodeOutlined
            aria-label={t('form.scarica-avviso', 'Scarica Avviso')}
          />
        }
        onClick={() => download()}
        disabled={!payment.id}
        loading={isFetching}
      />
    </Tooltip>
  );
}

function hasPayment(payment: PagamentoWithDettaglioDTO): boolean {
  const hasBurdenPayment =
    payment.oneri?.some(o => o.tipoOnere?.flPagamentoModello3) ?? false;
  const hasQuotePayment =
    payment.preventivo?.tipoPagamento?.flPagamentoModello3 ?? false;
  return hasBurdenPayment || hasQuotePayment;
}
